<script setup lang="ts">
import brandMark from './brand.png'
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthState } from '~/composables/useAuthState'
import MmIcon from '../base/MmIcon.vue'
import MmButton from '../base/MmButton.vue'
import MmSkeleton from '../base/MmSkeleton.vue'
import ShellAccountMenu from './ShellAccountMenu.vue'
import ShellStudioMenu from './ShellStudioMenu.vue'
import { primaryLinks, isShellRouteActive } from './navigation'

const { t } = useI18n()
const colorMode = useColorMode()
const route = useRoute()
const router = useRouter()
const { isAuthReady, isAuthenticated, isLoggingIn, handleSignIn, totalPoints, userAccount, isLoadingAccount } = useAuthState()
const search = ref<HTMLInputElement | null>(null)
const query = ref('')
const account = ref<InstanceType<typeof ShellAccountMenu> | null>(null)
const menuCount = useState('mm-open-menus', () => 0)
const tucked = ref(false)
const scrolled = ref(false)
let lastY = 0
const onScroll = () => {
  const y = window.scrollY
  scrolled.value = y > 8
  if (menuCount.value > 0 || window.innerWidth >= 1024 || route.path === '/create' || y <= 120) tucked.value = false
  else if (Math.abs(y - lastY) > 6) tucked.value = y > lastY
  lastY = y
}
const shortcut = (event: KeyboardEvent) => {
  if (event.defaultPrevented || event.repeat || event.metaKey || event.ctrlKey || event.altKey || event.key !== '/') return
  if ((event.target as HTMLElement)?.isContentEditable || (event.target as HTMLElement)?.closest('input, textarea, select, [role="textbox"], [role="dialog"], [role="menu"]')) return
  if (!search.value?.getClientRects().length) return
  event.preventDefault()
  search.value.focus()
}
const submit = () => router.push({ path: '/music/search', query: query.value.trim() ? { q: query.value.trim() } : {} })
watch(menuCount, count => { if (count) tucked.value = false })
watch(() => route.fullPath, () => { tucked.value = false })
onMounted(() => {
  onScroll()
  window.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('resize', onScroll)
  window.addEventListener('keydown', shortcut)
})
onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll)
  window.removeEventListener('resize', onScroll)
  window.removeEventListener('keydown', shortcut)
})
</script>

<template>
  <header class="topnav mm-root" :class="{ tuck: tucked, scrolled }" @focusin="tucked = false">
    <div class="wrap">
      <NuxtLink class="brand" to="/" :aria-label="t('t24a.homeLink')"><img :src="brandMark" alt="" width="32" height="32"><span>Music<b>Mint</b></span></NuxtLink>
      <nav class="pillnav" :aria-label="t('mm.shell.primary')">
        <NuxtLink v-for="link in primaryLinks" :key="link.to" :to="link.to" :class="{ 'create-link': link.to === '/create' }" :aria-current="isShellRouteActive(route.path, link.to) ? 'page' : undefined"><MmIcon v-if="link.to === '/create'" name="plus" :size="16" />{{ t(link.key) }}</NuxtLink>
        <ShellStudioMenu />
      </nav>
      <form class="search" role="search" @submit.prevent="submit"><label class="mm-sr-only" for="shell-search">{{ t('t24a.search') }}</label><MmIcon name="search" :size="18" /><input id="shell-search" ref="search" v-model="query" type="search" :placeholder="t('t24a.searchPlaceholder')"><kbd aria-hidden="true">/</kbd></form>
      <div class="right">
        <NuxtLink class="icon-btn mobile-search" to="/music/search" :aria-label="t('t24a.search')"><MmIcon name="search" /></NuxtLink>
        <NuxtLink class="btn btn-premium btn-sm upgrade" to="/pricing"><MmIcon name="zap" :size="16" />{{ t('mm.shell.upgrade') }}</NuxtLink>
        <ClientOnly>
          <button type="button" class="icon-btn theme-toggle" :aria-label="t('t24a.toggleTheme')" @click="colorMode.preference = colorMode.value === 'dark' ? 'light' : 'dark'"><MmIcon :name="colorMode.value === 'light' ? 'moon' : 'sun'" /></button>
          <template #fallback><span class="icon-btn theme-toggle" aria-hidden="true"></span></template>
        </ClientOnly>
        <ClientOnly>
          <div class="shell-auth-slot">
          <MmSkeleton v-if="!isAuthReady" class="shell-auth-placeholder" width="82px" height="44px" />
          <template v-else-if="isAuthenticated">
            <button type="button" class="credits shell-credits" :aria-label="t('t24a.creditsBalance')" :aria-busy="isLoadingAccount" @click="account?.openPoints()"><MmIcon name="zap" :size="16" /><span>{{ userAccount ? totalPoints : 0 }}</span></button>
            <ShellAccountMenu ref="account" />
          </template>
          <template v-else><MmButton class="shell-signin" size="sm" :loading="isLoggingIn" @click="handleSignIn"><span class="signin-short">{{ t('user.login') }}</span><span class="signin-full">{{ t('mm.shell.signIn') }}</span></MmButton><span class="guest-account-menu"><ShellAccountMenu /></span></template>
          </div>
          <template #fallback><div class="shell-auth-slot"><MmSkeleton class="shell-auth-placeholder" width="82px" height="44px" /></div></template>
        </ClientOnly>
      </div>
    </div>
  </header>
</template>

<style scoped>
.shell-auth-slot { min-height: 44px; min-width: 82px; display: flex; align-items: center; justify-content: flex-end; gap: var(--s-2); flex: 0 0 auto; }
.shell-auth-slot :deep(.shell-signin) { max-width: 100%; white-space: normal; line-height: 1.2; text-align: center; }
@media(max-width:1023px) { .guest-account-menu { display: none; } .shell-auth-slot { width: 82px; flex-basis: 82px; gap: var(--s-1); } }
@media (max-width: 1023px) {
  .topnav.mm-root a.btn.btn-sm.upgrade { display: inline-flex; min-height: 44px; padding-inline: var(--s-2); gap: var(--s-1); }
  .topnav.mm-root :is(a.btn.btn-sm.upgrade, button.btn.btn-sm.shell-signin) { --h: 44px; height: 44px; min-height: 44px; }
  .shell-auth-slot:has(.shell-signin) { min-width: 0; width: auto; flex-basis: auto; }
  .topnav .wrap { gap: var(--s-2); }
  .topnav .right { gap: var(--s-1); }
  .upgrade:focus-visible { outline: 2px solid var(--focus); outline-offset: 2px; }
}
@media (max-width: 479px) {
  .topnav .brand { gap: var(--s-2); font-size: var(--t-small); }
}
@media (max-width: 359px) {
  .topnav .wrap { gap: var(--s-1); }
  .topnav .brand { gap: var(--s-1); font-size: var(--t-small); }
  .topnav .brand span { display: none; }
  .topnav .right { gap: 0; }
  .topnav :deep(.shell-signin) { padding-inline: var(--s-3); }
}
</style>
